import { appendDebuggerFragment, encodeDebugPin } from '@shared/debugger/contract'
import type { DebugFocus, DebugPin } from '@shared/debugger/contract'
import api from './axiosApi'

/**
 * Opens the page in a new tab with a debugger session. The tab opens synchronously, inside the click, so pop-up
 * blockers allow it; `url` may be a function that resolves the address afterwards (a draft preview the server mints).
 */
export async function openDebuggerSession(
  teamPid: string | number,
  url: string | (() => Promise<string>),
  email?: string,
  focus?: DebugFocus,
  pin?: DebugPin | null
): Promise<void> {
  const given = typeof url === 'string' ? pageUrl(url) : null

  const tab = window.open('', '_blank')
  if (!tab) {
    throw new Error('Allow pop-ups to open the debugger.')
  }

  try {
    tab.opener = null
    const target = given ?? pageUrl(typeof url === 'string' ? url : await url())
    target.hash = appendDebuggerFragment(
      target.hash,
      null,
      focus ? `${focus.kind}:${focus.id}` : undefined,
      pin ? encodeDebugPin(pin) : undefined
    )
    const { data } = await api.post<{ token: string; expiresAt: string } | { authorizeUrl: string }>(
      `/api/${teamPid}/debugger/sessions`,
      { url: target.href, ...(email ? { email } : {}) }
    )
    if ('authorizeUrl' in data) {
      tab.location.href = data.authorizeUrl
    } else {
      target.hash = appendDebuggerFragment(target.hash, data.token)
      tab.location.href = target.href
    }
  } catch (error) {
    tab.close()
    throw error
  }
}

function pageUrl(url: string): URL {
  const target = new URL(url)
  if (target.protocol !== 'https:' && target.protocol !== 'http:') {
    throw new Error('The debugger requires an HTTP or HTTPS page URL.')
  }

  return target
}
